import { useEffect, useState } from 'react'
import {
  Controller,
  FieldValues,
  Path,
  PathValue,
  SubmitHandler,
  useForm,
  UseFormSetValue
} from 'react-hook-form'
import { toast } from 'react-toastify'

import {
  Button,
  Dialog,
  Loader,
  Select,
  Textfield
} from '@clientbase/clientbase-library'
import { yupResolver } from '@hookform/resolvers/yup'
import { Box, MenuItem, Stack } from '@mui/material'
// import { DataService } from 'api/DataService'
import { DataService } from 'api/DataService'
import * as yup from 'yup'

import InputController from 'components/InputController'

import { toastFirstError } from 'utils/yupResolverWithToast'

const typeOptions = [
  { label: 'Cartão de crédito', value: 'credit_card' },
  { label: 'Conta corrente', value: 'bank_account' },
  { label: 'Dinheiro', value: 'cash' },
  { label: 'Outro', value: 'other' }
]

// Campo numérico vazio vira NaN no yup e cairia no typeError em inglês
// ("must be a `number` type... NaN") antes de qualquer mensagem de obrigatório.
const emptyToUndefined = (value: unknown, original: unknown) =>
  original === '' || original === null ? undefined : value

const bankFieldsSchema = (requiredMessage: string, isRequired: boolean) =>
  yup.object({
    number: isRequired
      ? yup
          .number()
          .transform(emptyToUndefined)
          .typeError(requiredMessage)
          .required(requiredMessage)
      : yup.number().transform(emptyToUndefined),
    digits: yup
      .number()
      .transform(emptyToUndefined)
      .typeError('Dígito inválido')
  })

// O `when` perde a tipagem do objeto; o cast mantém `agency.number` etc. no tipo.
const bankFieldsWhen = (requiredMessage: string) =>
  yup.object().when('type', {
    is: 'bank_account',
    then: () => bankFieldsSchema(requiredMessage, true),
    otherwise: () => bankFieldsSchema(requiredMessage, false)
  }) as unknown as ReturnType<typeof bankFieldsSchema>

export const schemaSourceForm = yup.object({
  type: yup
    .string()
    .oneOf(typeOptions.map((type) => type.value))
    .required('O tipo é obrigatório'),
  name: yup.string().required('Apelido é obrigatório'),
  // Agência e conta só são obrigatórias para conta corrente.
  agency: bankFieldsWhen('Agência é obrigatória'),
  account: bankFieldsWhen('Conta é obrigatória')
})

export type SourceFormSchema = yup.InferType<typeof schemaSourceForm>

type SourceFormProps<T extends FieldValues> = {
  setValueFromOutside?: UseFormSetValue<T>
  onCreated?: (item: { uuid: string; name: string }) => void
  handleClose: () => void
  uuid?: string
  /**
   * Como a entidade se chama na tela que abriu o formulário. Em Contas a Pagar
   * é "Origem"; em Contas a Receber a mesma tabela é apresentada como "Conta".
   * O formulário é o mesmo, então o nome vem de fora — senão o modal diz
   * "Nova conta" e o diálogo de confirmação responde "Confirma criar uma Origem?".
   */
  entityLabel?: string
}

const SourceForm = <T extends FieldValues>({
  setValueFromOutside,
  onCreated,
  handleClose,
  uuid,
  entityLabel = 'Origem'
}: SourceFormProps<T>) => {
  const entityLabelLower = entityLabel.toLowerCase()
  const [loading, setLoading] = useState(false)
  const {
    control,
    watch,
    handleSubmit,
    clearErrors,
    reset,
    formState: { isDirty }
  } = useForm<SourceFormSchema>({
    defaultValues: {
      type: 'credit_card'
    },
    resolver: yupResolver(schemaSourceForm)
  })
  const [dialogOpen, setDialogOpen] = useState(false)

  // const validateBankAccount = () => {
  //   if (watch('type') == 'bank_account') {
  //     const fieldsToCheck = [
  //       { field: 'agency.number', error: 'Número é obrigatório' },
  //       { field: 'agency.digits', error: 'Dígito é obrigatório' },
  //       { field: 'account.number', error: 'Número é obrigatório' },
  //       { field: 'account.digits', error: 'Dígito é obrigatório' }
  //     ]

  //     let hasError = false

  //     fieldsToCheck.forEach((field: { field: any; error: string }) => {
  //       if (!watch(field.field)) {
  //         setError(field.field, { message: field.error })
  //         hasError = true
  //       }
  //     })

  //     if (hasError) {
  //       return false
  //     }
  //   }

  //   return true
  // }

  const handleClickToSubmit: SubmitHandler<SourceFormSchema> = async () => {
    setDialogOpen(true)
  }

  const handleSubmitForm: SubmitHandler<SourceFormSchema> = async (
    dataForm
  ) => {
    setLoading(true)

    const sourceTypeMapper = {
      credit_card: 1,
      bank_account: 2,
      cash: 3,
      other: 4
    }

    const data = {
      name: dataForm.name,
      source_type: sourceTypeMapper[dataForm.type],
      ...(dataForm.type == 'bank_account'
        ? {
            agency: dataForm.agency.number,
            agency_dv: dataForm.agency.digits,
            account: dataForm.account.number,
            account_dv: dataForm.account.digits
          }
        : {})
    }

    // O await é obrigatório: sem ele o setLoading(false) executa antes da resposta
    // e o botão reabilita com a requisição em voo (duplo clique = toast de erro ou duplicata).
    try {
      await toast.promise(
        async () => {
          const response = await DataService({
            data: data,
            type: uuid ? 'PATCH' : 'POST',
            url: uuid
              ? `/v1/accounts_payables/sources/${uuid}`
              : '/v1/accounts_payables/sources'
          })

          if (response.error) {
            setDialogOpen(false)
            throw new Error(
              response.error && (response.detail_error || response.message)
            )
          } else {
            // Com onCreated quem abriu o formulário decide onde gravar o item novo
            // (ex.: allocations.N.category); sem ele, grava no campo de raiz 'source'.
            if (onCreated) {
              onCreated({
                uuid: response.data.record.uuid,
                name: response.data.record.name
              })
            } else if (setValueFromOutside) {
              setValueFromOutside(
                'source' as Path<T>,
                {
                  label: response.data.record.name,
                  value: response.data.record.uuid
                } as PathValue<T, Path<T>>
              )
            }
            setDialogOpen(false)
            handleClose()
            return response.data.message
          }
        },
        {
          pending: `${uuid ? 'Atualizando' : 'Criando'} ${entityLabelLower}`,
          success: {
            // Texto próprio em vez da mensagem da API: ela sempre diz "Origem",
            // porque o endpoint é o de Contas a Pagar, e em Contas a Receber a
            // mesma entidade se chama Conta.
            render() {
              return `${entityLabel} ${
                uuid ? 'atualizada' : 'criada'
              } com sucesso.`
            }
          },
          error: {
            render({ data }) {
              return `${(data as any)?.message}`
            }
          }
        }
      )
    } catch {
      // erro já exibido pelo toast.promise
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!uuid) return

    const getSource = async () => {
      setLoading(true)
      const response = await DataService({
        type: 'GET',
        url: `/v1/accounts_payables/sources/${uuid}`
      })

      const record = response.data.record

      reset({
        name: record.name,
        type: record.source_type,
        agency: {
          number: record.source_type === 'bank_account' ? record.agency : '',
          digits: record.source_type === 'bank_account' ? record.agency_dv : ''
        },
        account: {
          number: record.source_type === 'bank_account' ? record.account : '',
          digits: record.source_type === 'bank_account' ? record.account_dv : ''
        }
      })

      setLoading(false)
    }

    getSource()
  }, [uuid])

  useEffect(() => {
    clearErrors('account')
  }, [watch('type')])

  if (loading) {
    return <Loader />
  }

  return (
    <Stack justifyContent="space-between" flexGrow={1} gap="22px">
      <Stack gap="12px">
        <Controller
          control={control}
          name="type"
          render={({ field }) => (
            <Select
              fullWidth={true}
              label="Tipo"
              placeholder="Selecione o tipo da conta"
              {...field}
            >
              {typeOptions.map(({ label, value }, index) => (
                <MenuItem key={index} value={value}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          )}
        />

        <InputController control={control} name="name">
          <Textfield label="Apelido" placeholder="Digite um apelido" />
        </InputController>

        {watch('type') == 'bank_account' && (
          <>
            {' '}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '10fr 2fr',
                gap: '10px'
              }}
            >
              <>
                <InputController control={control} name="agency.number">
                  <Textfield label="Agência" type="number" placeholder="0000" />
                </InputController>

                <InputController control={control} name="agency.digits">
                  <Textfield
                    label={<span style={{ color: 'transparent' }}>.</span>}
                    type="number"
                    placeholder="0"
                  />
                </InputController>
              </>
            </Box>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '10fr 2fr',
                gap: '10px'
              }}
            >
              <>
                <InputController control={control} name="account.number">
                  <Textfield
                    label="Conta"
                    type="number"
                    placeholder="00000000"
                  />
                </InputController>

                <InputController control={control} name="account.digits">
                  <Textfield
                    label={<span style={{ color: 'transparent' }}>.</span>}
                    type="number"
                    placeholder="0"
                  />
                </InputController>
              </>
            </Box>
          </>
        )}
      </Stack>

      <Stack direction="row" gap={1}>
        <Button fullWidth variantButton="neutral" onClick={handleClose}>
          Cancelar
        </Button>
        <Button
          fullWidth
          disabled={loading || !isDirty}
          onClick={handleSubmit(handleClickToSubmit, toastFirstError)}
          variantButton="primary"
        >
          {uuid ? 'Atualizar' : 'Salvar'}
        </Button>
      </Stack>

      <Dialog
        icon={uuid ? 'edit' : 'add'}
        title={entityLabel}
        description={
          uuid
            ? `Confirma atualizar essa ${entityLabel}?`
            : `Confirma criar uma ${entityLabel}?`
        }
        setIsOpenDialog={setDialogOpen}
        isOpenDialog={dialogOpen}
        cancelButton
      >
        <Button
          autoFocus
          disabled={loading}
          loading={loading}
          onClick={handleSubmit(handleSubmitForm, toastFirstError)}
          fullWidth
        >
          {uuid ? 'Atualizar' : 'Criar'}
        </Button>
      </Dialog>
    </Stack>
  )
}

export default SourceForm
